import { FC, useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { alpha } from '@mui/material/styles'
import CloseIcon from '@mui/icons-material/Close'
import useTranslate from '@/hooks/use-translate'
import { getNf, nf, NfTokens } from '../nfTokens'

const formatFileSize = (bytes: number) =>
  bytes > 1048576
    ? `${(bytes / 1048576).toFixed(1)} Mo`
    : `${Math.round(bytes / 1024)} Ko`

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png']
const SHEET_EXTENSIONS = ['xls', 'xlsx', 'ods', 'csv']

type FileKind = 'pdf' | 'image' | 'sheet' | 'zip' | 'generic'

const fileKind = (extension: string): FileKind => {
  if (extension === 'pdf') return 'pdf'
  if (IMAGE_EXTENSIONS.includes(extension)) return 'image'
  if (SHEET_EXTENSIONS.includes(extension)) return 'sheet'
  if (extension === 'zip') return 'zip'
  return 'generic'
}

/** Pastille de type : l'extension en toutes lettres, teintée par famille, se lit mieux qu'un pictogramme de 14 px. */
const kindColors = (kind: FileKind, t: NfTokens) => {
  switch (kind) {
    case 'pdf':
      return { color: t.danger, bgcolor: t.dangerBg }
    case 'sheet':
      return { color: t.ok, bgcolor: alpha(t.ok, 0.14) }
    case 'image':
      return { color: t.darkGreen, bgcolor: t.greenTint }
    case 'zip':
      return { color: t.warn, bgcolor: t.warnBg }
    default:
      return { color: t.ink3, bgcolor: t.line2 }
  }
}

/** Aperçu d'une image locale ; `null` si ce n'est pas une image. */
const useImagePreview = (file: File) => {
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    // jsdom n'a pas createObjectURL : la pastille suffit.
    if (!file.type.startsWith('image/') || typeof URL.createObjectURL !== 'function') return undefined
    const objectUrl = URL.createObjectURL(file)
    setUrl(objectUrl)
    return () => {
      URL.revokeObjectURL(objectUrl)
      setUrl(null)
    }
  }, [file])
  return url
}

export interface NfFileRowProps {
  file: File
  onRemove: () => void
}

export const NfFileRow: FC<NfFileRowProps> = ({
  file,
  onRemove,
}) => {
  const { t: tUpload } = useTranslate('upload')
  const previewUrl = useImagePreview(file)
  // Image illisible (HEIC renommé, fichier tronqué) : retour à la pastille.
  const [previewFailed, setPreviewFailed] = useState(false)
  const extension = file.name.includes('.') ? file.name.split('.').pop()?.toLowerCase() ?? '' : ''
  const kind = fileKind(extension)

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.25}
      sx={(theme) => {
        const t = getNf(theme)
        return {
          p: '9px 11px',
          borderRadius: `${nf.radiusCard}px`,
          border: `1px solid ${t.line}`,
          bgcolor: t.surface,
        }
      }}
    >
      <Box
        sx={(theme) => {
          const t = getNf(theme)
          return {
            width: 32,
            height: 32,
            borderRadius: `${nf.radiusField}px`,
            ...kindColors(kind, t),
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
            overflow: 'hidden',
          }
        }}
      >
        {previewUrl && !previewFailed ? (
          <Box
            component="img"
            src={previewUrl}
            alt=""
            onError={() => setPreviewFailed(true)}
            data-testid="file-row-preview"
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <Typography
            component="span"
            data-testid={`file-kind-${kind}`}
            sx={{ fontSize: 9, fontWeight: 700, letterSpacing: '.3px', lineHeight: 1 }}
          >
            {extension.slice(0, 4).toUpperCase() || '—'}
          </Typography>
        )}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={(theme) => ({
            fontSize: 11.5,
            fontWeight: 500,
            color: getNf(theme).ink,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          })}
        >
          {file.name}
        </Typography>
        <Typography
          sx={(theme) => ({ mt: '3px', fontSize: 10, color: getNf(theme).muted })}
        >
          {formatFileSize(file.size)}
        </Typography>
      </Box>
      <IconButton
        size="small"
        aria-label={tUpload('wizardRemoveFile')}
        title={tUpload('wizardRemoveFile')}
        onClick={onRemove}
        data-testid="file-input-remove-button"
        sx={(theme) => ({ color: getNf(theme).muted2, flexShrink: 0 })}
      >
        <CloseIcon sx={{ fontSize: 15 }} />
      </IconButton>
    </Stack>
  )
}

export default NfFileRow
